/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 17-Jul-2026
 * @author      : Vincy R
 * Requirement# : 22300
 * Purpose      : REST client for the versioned Trade Template Builder endpoints
 *                (draft / publish / makeCurrent / promote / resolve / getDraft).
 * -----------------------------------------------------------------------------
 * Revision History
 * -----------------------------------------------------------------------------
 * Requirement/
 * Issue/WorkItem |   DATE      |   AUTHOR    |   DESCRIPTION OF CHANGE
 * -----------------------------------------------------------------------------
 * 22300          | 17-Jul-2026 | Vincy R     | Initial creation
 * AB#32844       | 05-Oct-2026 | Vincy R     | is_system_task persisted verbatim;
 *                |             |             | dependency relation suffix preserved
 */

import { executeGetData, executePostData } from '../../../services/ApiBase';
import { ConfigUrl } from '../../../auth/OidcConfig';
import { Task, Scope, PhaseId, TemplateVersion, DependencyClass, AseVersion } from '../types';
import { deriveParentUid, isMilestoneUid, computeEstimate, roundEstimate } from '../model';

const BASE = `${ConfigUrl.baseURL}/taskMapping`;

/** Uniform result shape returned by ApiBase.execute. */
export interface ApiResult<T = unknown> { ok: boolean; data?: T; status?: number; message?: string; }

/** One phase-entry as returned by GET currentTemplate. */
export interface TemplateEntry { name: string; tasks: string; templateVersion?: number | null; versionGroupUid?: string | null; }
export interface ResolveResult { source: 'provider' | 'global' | 'none'; tradeId: number; entries: TemplateEntry[]; }

/** Optional source lineage — set when a save copies a template from another scope/tenant into this one. */
export interface SourceLineage { sourceScope?: string; sourceVersion?: number | null; sourceVersionGroupUid?: string | null; sourceTenantId?: string | null; }
export interface SaveDraftPayload extends SourceLineage { name?: string; tasks: string; scope: Scope; tenantId?: string | null; updatedBy?: string; }
export interface PublishDraftPayload { scope: Scope; tenantId?: string | null; updatedBy?: string; note?: string | null; }
export interface MakeCurrentPayload { versionGroupUid: string; scope: Scope; tenantId?: string | null; updatedBy?: string; }
export interface PromotePayload { sourceTenantId: string; sourceVersionGroupUid?: string | null; updatedBy?: string; }

/** Save (upsert in place) the working draft of a trade template for a scope. */
export function saveDraft(tradeId: number, payload: SaveDraftPayload, accessToken: string): Promise<ApiResult> {
  return executePostData(`${BASE}/trade/${tradeId}/draft`, payload, accessToken);
}

/** Publish the working draft as a new immutable version. */
export function publishDraft(tradeId: number, payload: PublishDraftPayload, accessToken: string): Promise<ApiResult> {
  return executePostData(`${BASE}/trade/${tradeId}/publishDraft`, payload, accessToken);
}

/** Make a specific version (by version_group_uid) the current one. No new version created. */
export function makeCurrent(tradeId: number, payload: MakeCurrentPayload, accessToken: string): Promise<ApiResult> {
  return executePostData(`${BASE}/trade/${tradeId}/makeCurrent`, payload, accessToken);
}

/** Promote a tenant version into the trade's provider default. */
export function promote(tradeId: number, payload: PromotePayload, accessToken: string): Promise<ApiResult> {
  return executePostData(`${BASE}/trade/${tradeId}/promote`, payload, accessToken);
}

/** Resolve the current template for a trade (provider default, else global default). */
export function resolveCurrentTemplate(tradeId: number, accessToken: string): Promise<ApiResult<ResolveResult>> {
  return executeGetData(`${BASE}/trade/${tradeId}/currentTemplate`, accessToken);
}

/**
 * Bulk-resolve the current template for MANY trades in one request (vs. one resolveCurrentTemplate
 * call per trade). Built for the Provider library grid, which previously opened up to ~2 DB
 * connections PER TRADE in parallel on every page load (resolveCurrentTemplate + getDraft) — with a
 * large trade catalog, that concurrent burst was enough to exhaust Postgres's max_connections.
 * @return tradeId -> ResolveResult (same shape resolveCurrentTemplate returns for one trade)
 */
export function resolveCurrentTemplatesBulk(tradeIds: number[], accessToken: string): Promise<ApiResult<Record<number, ResolveResult>>> {
  const query = tradeIds.map(id => `tradeIds=${id}`).join('&');
  return executeGetData(`${BASE}/trade/currentTemplates?${query}`, accessToken);
}

export interface BulkDraftResult { tradeId: number; hasDraft: boolean; updatedAt?: string | null; updatedBy?: string | null; entries: TemplateEntry[]; }

/** Bulk-read the saved provider-scope draft for MANY trades in one request (vs. one getDraft call per trade). */
export function getProviderDraftsBulk(tradeIds: number[], accessToken: string): Promise<ApiResult<Record<number, BulkDraftResult>>> {
  const query = tradeIds.map(id => `tradeIds=${id}`).join('&');
  return executeGetData(`${BASE}/trade/providerDrafts?${query}`, accessToken);
}

/** One logical version as returned by GET versions. */
export interface ApiVersion {
  version: number | null; versionGroupUid: string | null; invalid: boolean; current: boolean;
  updatedBy?: string | null; when?: string | null; note?: string | null;
  forkedFrom?: { scope: string; version: number | null; versionGroupUid?: string | null; tenantId: string | null } | null;
  entries: TemplateEntry[];
}
export interface ListVersionsResult { tradeId: number; scope: Scope; tenantId: string | null; versions: ApiVersion[]; }

/** List the version history for a scope (provider/global/tenant), with each version's per-phase tasks. */
export function listVersions(tradeId: number, scope: Scope, tenantId: string | null, accessToken: string): Promise<ApiResult<ListVersionsResult>> {
  const query = tenantId ? `?scope=${scope}&tenantId=${encodeURIComponent(tenantId)}` : `?scope=${scope}`;
  return executeGetData(`${BASE}/trade/${tradeId}/versions${query}`, accessToken);
}

/** Result of GET .../draft — the current saved (status='draft') work-in-progress for a scope. */
export interface DraftResult {
  tradeId: number; scope: Scope; tenantId: string | null;
  hasDraft: boolean; updatedAt?: string | null; updatedBy?: string | null;
  forkedFrom?: { scope: string; version: number | null; versionGroupUid?: string | null; tenantId: string | null } | null;
  entries: TemplateEntry[];
}

/** Read the current saved draft for a scope so the Builder can resume it / the Library can flag it. */
export function getDraft(tradeId: number, scope: Scope, tenantId: string | null, accessToken: string): Promise<ApiResult<DraftResult>> {
  const query = tenantId ? `?scope=${scope}&tenantId=${encodeURIComponent(tenantId)}` : `?scope=${scope}`;
  return executeGetData(`${BASE}/trade/${tradeId}/draft${query}`, accessToken);
}

/** Map the API version rows into the UI TemplateVersion contract (oldest first, as returned). */
export function mapApiVersions(list: ApiVersion[] | undefined | null): TemplateVersion[] {
  if (!list) return [];
  return list.map(v => ({
    version: `v${v.version ?? 1}`,
    note: v.note || (v.updatedBy ? `Saved by ${v.updatedBy}` : 'Version'),
    when: v.when ?? '',
    tasks: flattenEntriesToTasks(v.entries),
    forkedFrom: v.forkedFrom
      ? { scope: v.forkedFrom.scope as Scope, version: v.forkedFrom.version != null ? `v${v.forkedFrom.version}` : '', tenantId: v.forkedFrom.tenantId ?? undefined }
      : null,
    status: 'active' as const,
    invalid: v.invalid,
    versionGroupUid: v.versionGroupUid,
  }));
}

/** Direct save + publish to provider master and selected tenants (the original 22300 path). */
export function saveTradeTemplate(
  tradeId: number,
  payload: { name?: string; tasks: string; tenantIds?: string[]; updatedBy?: string; note?: string } & SourceLineage,
  accessToken: string,
): Promise<ApiResult> {
  return executePostData(`${BASE}/saveTradeTemplate/tradeId/${tradeId}`, payload, accessToken);
}

const VALID_PHASES: PhaseId[] = ['sales', 'preConstruction', 'activeConstruction', 'postConstruction'];

/** Split one stored token into its numeric head and its relation suffix.
 *  '23000FS+40h' -> [23000, 'FS+40h'];  '49000' -> [49000, ''];  anything else -> null. */
function splitDependencyToken(token: string): [number, string] | null {
  const m = /^(\d+)(.*)$/.exec(token);
  if (!m) return null;
  const head = Number(m[1]);
  return Number.isFinite(head) ? [head, m[2]] : null;
}

/**
 * Dependencies are stored in the legacy ASE contract as a ";"-joined string of the referenced tasks'
 * {@code task_order} values ("" when none, e.g. "4000;14000"), each optionally carrying a relation
 * suffix ("23000FS+40h"). Builder-saved versions may already hold a number[]. Accept both and always
 * return number[] so the UI/graph logic is uniform; without this, string-form dependencies (the
 * seeded Global Default + every legacy template) silently drop to [] and no dependency ever renders.
 * The suffix is kept separately — see {@link parseDependencyRelations}.
 */
function parseDependencies(raw: unknown): number[] {
  return dependencyTokens(raw)
    .map(t => splitDependencyToken(t))
    .filter((p): p is [number, string] => p !== null)
    .map(([head]) => head);
}

/** The relation suffix for each dependency, keyed by its numeric head.
 *
 *  ASE treats the suffix as part of the token: shared/engine/po_task_cleanup._token_head
 *  reads a dependency as `<id><relation-suffix>`, e.g. '34000+2h'. It is scheduling data the
 *  Builder carries but never interprets, so it must survive a load/save round trip intact.
 */
function parseDependencyRelations(raw: unknown): Record<number, string> {
  const out: Record<number, string> = {};
  for (const token of dependencyTokens(raw)) {
    const parsed = splitDependencyToken(token);
    if (parsed && parsed[1] !== '') out[parsed[0]] = parsed[1];
  }
  return out;
}

function dependencyTokens(raw: unknown): string[] {
  const parts = Array.isArray(raw) ? raw : typeof raw === 'string' ? raw.split(';') : [];
  return parts
    .map(v => String(v).trim())
    .filter(s => s !== '');         // "".split(';') -> [""]; drop empties so we don't coerce to 0
}

/**
 * Normalize a raw task object (from a stored template, which may be a legacy shape) into the Task
 * contract the Builder expects, filling safe defaults for any missing field so the UI never crashes
 * on partial data. Unknown phases fall back to 'preConstruction'.
 */
function normalizeTask(raw: Record<string, unknown>, index: number): Task {
  const uid = String(raw.task_uid ?? raw.taskUid ?? index + 1);
  const phase = VALID_PHASES.includes(raw.phase as PhaseId) ? (raw.phase as PhaseId) : 'preConstruction';
  return {
    task_uid: uid,
    parent_task_uid: String(raw.parent_task_uid ?? deriveParentUid(uid)),
    taskName: String(raw.taskName ?? raw.task_name ?? raw.name ?? `Task ${uid}`),
    phase,
    task_order: Number(raw.task_order ?? (index + 1) * 100),
    dependencies: parseDependencies(raw.dependencies),
    dependencyRelations: parseDependencyRelations(raw.dependencies),
    dependency_classes: (raw.dependency_classes && typeof raw.dependency_classes === 'object'
      ? raw.dependency_classes : {}) as Task['dependency_classes'],
    // Rounded on load too — a template saved before roundEstimate existed may already have
    // floating-point drift (e.g. 0.10600000000001) baked into a leaf's stored value.
    estimatedDays: roundEstimate(Number(raw.estimatedDays ?? raw.estimated_days ?? 0)),
    estimatedHours: roundEstimate(Number(raw.estimatedHours ?? raw.estimated_hours ?? 0)),
    timeLine: String(raw.timeLine ?? raw.timeline ?? ''),
    taskType: (raw.taskType ?? raw.task_type ?? '') as Task['taskType'],
    is_milestone: !!raw.is_milestone,
    is_system_task: !!raw.is_system_task,
    is_compressible: !!raw.is_compressible,
    min_workers: Number(raw.min_workers ?? 1),
    max_workers: Number(raw.max_workers ?? 1),
    compression_factor: Number(raw.compression_factor ?? 1),
    resources: Array.isArray(raw.resources) ? (raw.resources as Task['resources']) : [],
    actions: Array.isArray(raw.actions) ? (raw.actions as Task['actions']) : [],
    bom: Array.isArray(raw.bom) ? (raw.bom as unknown[]) : [],
    additionalData: (raw.additionalData && typeof raw.additionalData === 'object' ? raw.additionalData : {}) as Record<string, unknown>,
    checklistItems: Array.isArray(raw.checklistItems)
      ? (raw.checklistItems as unknown[]).map((c, i) => {
          const item = (c && typeof c === 'object' ? c : {}) as Record<string, unknown>;
          return { label: String(item.label ?? ''), order: Number(item.order ?? i + 1) };
        })
      : [],
    valid: raw.valid !== false,
    status: raw.status === 'inactive' ? 'inactive' : 'active',
  };
}

/**
 * Flatten the per-phase entries of a resolved/version template into one normalized Task[] for the
 * Builder. Each entry's {@code tasks} column is a JSON array of tasks; concatenate them in entry
 * order. Malformed entries are skipped (never throws) so a single bad row can't blank the builder.
 */
export function flattenEntriesToTasks(entries: TemplateEntry[] | undefined | null): Task[] {
  if (!entries) return [];
  const out: Task[] = [];
  for (const entry of entries) {
    try {
      const parsed = JSON.parse(entry.tasks ?? '[]');
      if (Array.isArray(parsed)) {
        parsed.forEach((t, i) => out.push(normalizeTask((t ?? {}) as Record<string, unknown>, out.length + i)));
      }
    } catch {
      // skip malformed entry — keep the rest of the template usable
    }
  }
  return out;
}

/**
 * Serialize the builder's Task[] to the EXACT stored JSON contract ASE consumes. This emits only the
 * template's own 21 fields — the builder's UI-only fields ({@code valid}, {@code status}) are dropped,
 * so loading a template and saving it unchanged round-trips identically (no foreign attributes leak in).
 * Legacy representations are preserved so an unedited task is byte-stable:
 *   • is_milestone — derived: true iff the task has children (isMilestoneUid). ASE reads it verbatim
 *     and uses it in scheduling (taskUtils.py), so it must reflect the hierarchy, not a stale value.
 *   • is_system_task — NOT derived: persisted verbatim from the task. It is an authored
 *     flag, so a save must never infer it from the task type.
 *   • dependencies — ";"-joined task_order string ("" when none), the shape parseDependencies() reads.
 *   • resources / actions / additionalData — empty collections are stored as "" (the legacy empty form),
 *     non-empty ones as the array/object; bom stays [] and dependency_classes stays {} (their legacy forms).
 * task_order is CANONICALIZED: tasks are sorted, then re-assigned clean, unique 1000-gap orders
 * (1000, 2000, 3000, … across the whole template — the same convention the seed/published templates use).
 * Every dependency reference (both `dependencies` and `dependency_classes` keys) is remapped through the
 * old→new order map, so an inserted sub-task (e.g. one at 2100) yields clean numbering with the
 * dependency graph fully preserved — no duplicate/overflowing orders, no dangling references.
 */
export function serializeTasksForStorage(tasks: Task[], aseVersion: AseVersion = '2.0'): string {
  const ordered = [...tasks].sort((a, b) => a.task_order - b.task_order);
  // old task_order -> new clean 1000-gap order. Dependencies reference task_order, so the map is
  // order-keyed. Old orders SHOULD be unique, but a transient duplicate (bad seed / mid-insert) must
  // not let the second task silently overwrite the mapping and repoint every dependency to the LATER
  // task — FIRST occurrence (earliest in sorted order) wins deterministically instead.
  const orderMap = new Map<number, number>();
  ordered.forEach((t, i) => { if (!orderMap.has(t.task_order)) orderMap.set(t.task_order, (i + 1) * 1000); });
  const remap = (o: number) => orderMap.get(o) ?? o; // fall back to the original if somehow unmapped

  const out = ordered.map((t, i) => {
    // A parent's Est. days/hours are DERIVED from its children (see computeEstimate) — the Inspector
    // never lets them be edited directly once it has children, so its own stored field can be stale;
    // always persist the computed total instead of trusting it.
    const estimate = isMilestoneUid(tasks, t.task_uid) ? computeEstimate(tasks, t.task_uid) : null;
    // Base = the legacy ASE 1.0 (14-field) contract.
    const base: Record<string, unknown> = {
      taskName: t.taskName,
      // Remap the numeric head, keep the relation suffix verbatim. A dependency the
      // author added has no suffix; one they removed simply leaves its entry unused.
      dependencies: (t.dependencies ?? [])
        .map(d => `${remap(d)}${t.dependencyRelations?.[d] ?? ''}`)
        .join(';'),
      timeLine: t.timeLine ?? '',
      estimatedDays: estimate ? estimate.days : roundEstimate(t.estimatedDays),
      estimatedHours: estimate ? estimate.hours : roundEstimate(t.estimatedHours),
      taskType: t.taskType,
      resources: t.resources?.length ? t.resources : '',
      actions: t.actions?.length ? t.actions : '',
      additionalData: t.additionalData && Object.keys(t.additionalData).length ? t.additionalData : '',
      phase: t.phase,
      task_order: (i + 1) * 1000,                    // clean, unique, sorted
      task_uid: t.task_uid,
      parent_task_uid: t.parent_task_uid,
      is_milestone: isMilestoneUid(tasks, t.task_uid),
      // Field-crew checklist — not a scheduling attribute, so present in both ASE 1.0 and 2.0 (unlike
      // the seven gated-below CP-SAT fields). order is always renumbered by array position, never
      // trusted from the loaded value, so it can't drift from the UI's actual list order.
      checklistItems: (t.checklistItems ?? []).map((c, ci) => ({ label: c.label, order: ci + 1 })),
    };
    // ASE 2.0 adds the seven CP-SAT scheduling attributes. ASE 1.0 omits them entirely so the saved
    // template stays the legacy 14-field shape (no is_compressible / workers / compression / bom /
    // dependency_classes / is_system_task).
    if (aseVersion === '2.0') {
      const depClasses: Record<number, DependencyClass> = {};
      Object.entries(t.dependency_classes ?? {}).forEach(([k, v]) => { depClasses[remap(Number(k))] = v as DependencyClass; });
      base.is_compressible = t.is_compressible;
      base.min_workers = t.min_workers;
      base.max_workers = t.max_workers;
      base.compression_factor = t.compression_factor;
      base.bom = t.bom ?? [];
      base.dependency_classes = depClasses;
      // The author's own flag, not a property of the task type: two tasks sharing a
      // type may legitimately differ, so it is persisted exactly as the template holds it.
      base.is_system_task = !!t.is_system_task;
    }
    return base;
  });
  return JSON.stringify(out);
}
